import CheckCircle from '@betfan/icons/linear/EssentionalUI/CheckCircle.svg?react';
import Close from '@betfan/icons/linear/EssentionalUI/Close.svg?react';
import DangerCircle from '@betfan/icons/linear/EssentionalUI/DangerCircle.svg?react';
import DangerTriangle from '@betfan/icons/linear/EssentionalUI/DangerTriangle.svg?react';
import InfoCircle from '@betfan/icons/linear/EssentionalUI/InfoCircle.svg?react';
import cn from 'classnames';
import React, { PropsWithChildren, Ref } from 'react';
import { DSSpinner } from './animations';

export type DSToastType = 'success' | 'info' | 'warning' | 'error';

type BaseProps = Pick<React.HTMLAttributes<HTMLDivElement>, 'className'> & {
	type: DSToastType;
	innerRef?: Ref<HTMLDivElement>;
	cyTestRef?: string;
};

type DSToastProps = BaseProps &
	(
		| {
				spinner: true;
		  }
		| {
				spinner?: false;
				onClose: () => void;
		  }
	);

export const DSToast = (props: PropsWithChildren<DSToastProps>) => {
	const colorClasses = () => {
		switch (props.type) {
			case 'success':
				return 'bg-success-300 text-success-700 dark:bg-success-800 dark:text-success-500';
			case 'info':
				return 'bg-info-300    text-info-700    dark:bg-info-800    dark:text-info-500';
			case 'warning':
				return 'bg-warning-300 text-warning-700 dark:bg-warning-800 dark:text-warning-500';
			case 'error':
				return 'bg-error-300   text-error-700   dark:bg-error-800   dark:text-error-500';
		}
	};

	const icon = () => {
		const iconClasses = 'w-4 h-4';
		switch (props.type) {
			case 'success':
				return <CheckCircle className={iconClasses} />;
			case 'info':
				return <InfoCircle className={iconClasses} />;
			case 'warning':
				return <DangerTriangle className={iconClasses} />;
			case 'error':
				return <DangerCircle className={iconClasses} />;
		}
	};

	const classes = () => cn('flex items-stretch justify-between', 'font-medium', props.className);

	const textWrapperClasses = () => cn('flex flex-grow items-center pl-2 rounded-l-8', colorClasses());

	const rightSupportClasses = 'px-3 py-3 flex items-center justify-center min-h-full rounded-r-8';

	const spinnerClasses = () => cn(rightSupportClasses, colorClasses());

	const closeButtonClasses = () =>
		cn(rightSupportClasses, {
			'bg-success-300 text-success-700 dark:bg-success-800 dark:text-success-500': props.type === 'success',
			'bg-info-300    text-info-700    dark:bg-info-800    dark:text-info-500': props.type === 'info',
			'bg-warning-300 text-warning-700 dark:bg-warning-800 dark:text-warning-500': props.type === 'warning',
			'bg-error-300   text-error-700   dark:bg-error-800   dark:text-error-500': props.type === 'error',
		});

	return (
		<div className={classes()} ref={props.innerRef} data-cy={props.cyTestRef}>
			<div className={textWrapperClasses()}>
				<div className="flex items-center">{icon()}</div>
				<div className="ml-3 mr-4 my-2 text-paragraph-sm">{props.children}</div>
			</div>
			{props.spinner ? (
				<div className={spinnerClasses()}>
					<DSSpinner className="w-6 h-6" />
				</div>
			) : (
				<button className={closeButtonClasses()} onClick={props.onClose}>
					<Close className="w-6 h-6" />
				</button>
			)}
		</div>
	);
};
